import { Component, OnDestroy, OnInit } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { Network } from '@capacitor/network';

import { LoadingController } from '@ionic/angular';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import dayjs from 'dayjs';

import { AlertService } from '@core/services/alert/alert.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { ApplicationOnCredit } from '@core/services/online-credit/online-credit.model';
import { OnlineCreditService } from '@core/services/online-credit/online-credit.service';
import { StepManagerService } from '@core/services/online-credit/step-manager.service';
import { MaskUtilService } from '@core/services/utils/mask-util.service';
import { UtilsService } from '@core/services/utils/utils.service';

import { LifecycleHookLogs } from '@shared/decorators/lifecycle-hook-logs/lifecycle-hook-logs.decorator';
import { NetworkService } from '@shared/services/network.service';

import {
  CalculationConfig,
  CalculationResult,
  CalculationSettings,
  CreateApplicationRequest
} from '../store/store.model';
import { StoreService } from '../store/store.service';
import { LoanCalculationService } from './loan-calculation.service';

@LifecycleHookLogs()
@UntilDestroy()
@Component({
    selector: 'app-loan-calculation-step',
    templateUrl: 'loan-calculation-step.page.html',
    styleUrls: ['./loan-calculation-step.page.scss', '../new-credit.scss']
    // changeDetection: ChangeDetectionStrategy.OnPush
    ,
    standalone: false
})
export class LoanCalculationStepPage implements OnInit, OnDestroy {
  title: string = 'Кредит наличными';
  subtitle: string = '';

  public loaded$: Observable<boolean>;

  public amountMask: any;
  public showMoreAmountMaxMessage: boolean;
  public application: ApplicationOnCredit;
  public expDateApplication;
  public isPromotionalApprovedRate: boolean;

  private config$: Observable<CalculationConfig>;
  private calculationResult$: Observable<CalculationResult>;

  public settings$: Observable<CalculationSettings>;

  public agreeInsuranceInfo = {
    title: 'Страхование жизни и здоровья (включено в сумму кредита)',
    text: `<h4>Информация о страховании</h4>
    <p>Банк оказывает дополнительные услуги по присоединению заемщика к программам добровольного коллективного страхования, включая следующие услуги: сбор, обработка, техническая передача информации о заемщиках, связанная с распространением на них соответствующей программы добровольного коллективного страхования, оформление необходимых документов.
    Заемщик вправе отказаться от услуги по добровольному коллективному страхованию в течение четырнадцати календарных дней со дня подписания заявления на присоединение к соответствующей программе добровольного коллективного страхования посредством подачи Банку заявления об исключении его из числа застрахованных лиц по личному страхованию.
    Заемщик ознакомлен с тем, что он может получить потребительский кредит без оформления присоединения к программе добровольного коллективного страхования с увеличением размера процентной ставки по договору потребительского кредита относительно размера процентной ставки по договору потребительского кредита, заключенному с предоставлением заемщику данной услуги по страхованию, а также с тем, что личное страхование не является обязательным условием для получения кредита и отказ от страхования не является основанием для отказа в выдаче кредита</p>`
  };

  public isApproved: boolean;

  constructor(
    private stepManagerService: StepManagerService,
    private loanCalculationService: LoanCalculationService,
    private onlineCreditService: OnlineCreditService,
    public utilService: UtilsService,
    public loadingCtrl: LoadingController,
    public utils: UtilsService,
    private store: StoreService,
    private alertService: AlertService,
    private navigationService: NavigationService,
    private networkService: NetworkService
  ) {}

  ngOnInit() {
    this.loaded$ = this.stepManagerService.stepLoaded$;
    this.loanCalculationService.settings$.pipe(untilDestroyed(this)).subscribe(({ application, settings }) => {
      this.store.updateCalculationConfig(settings);
      this.store.updateApplication(application);
      this.config$ = this.store.calculationConfig$;
      this.calculationResult$ = this.store.calculationResult$;
      this.application = this.store.value.application;
      this.expDateApplication = this.application
        ? dayjs(this.application.changeStatusDate).add(30, 'days').format('DD.MM.YYYY')
        : null;
      this.isApproved = this.loanCalculationService.isApprovedApplication;
      this.isPromotionalApprovedRate = this.loanCalculationService.isPromotionalApprovedRate;
      const { amount, period, insurance } = this.store.value.calculationConfig;

      this.settings$ = this.calculationResult$.pipe(
        withLatestFrom(this.config$),
        map(([calculationResult, config]) => {
          return {
            ...config,
            ...calculationResult
          };
        }),
        shareReplay(1)
      );
      this.calculateLoan(amount, period, insurance, false);
      this.stepManagerService.setStepLoaded(true);
    });
  }

  ionViewWillEnter() {}

  ngOnDestroy() {}

  ionViewWillLeave() {}

  /**
   * Обрабатывает нажатие клавиши Enter для полей ввода суммы или периода.
   * @param event - Событие клавиатуры.
   * @param inputName - Имя поля ввода ('amount' или 'period').
   */
  handleKeypress(event: KeyboardEvent, inputName: 'amount' | 'period') {
    if (event.keyCode === 13) {
      event.preventDefault();
      switch (inputName) {
        case 'amount':
          this.changeAmount(event);
          break;
        case 'period':
          this.changePeriod(event);
          break;
      }
    }
  }

  /**
   * Обрабатывает изменение суммы кредита.
   * @param event - Событие изменения суммы.
   */
  changeAmount(event) {
    const value = this.extractAmountValueFromEvent(event);
    const { period, insurance } = this.store.value.calculation;
    this.calculateLoan(value, period, insurance, false);
  }

  /**
   * Обрабатывает изменение периода кредита.
   * @param event - Событие изменения периода.
   */
  changePeriod(event) {
    const value = this.extractPeriodValueFromEvent(event);
    const periodHasBeenIncreased = this.periodHasBeenIncreased(value);
    const { amount, insurance } = this.store.value.calculation;
    this.calculateLoan(amount, value, insurance, periodHasBeenIncreased);
  }

  /**
   * Обрабатывает изменение состояния страховки.
   * @param $event - Событие изменения страховки.
   */
  changeInsurance($event) {
    const { amount, period } = this.store.value.calculation;
    this.calculateLoan(amount, period, $event, false);
  }

  /**
   * Создает заявку на кредит, проверяя подключение к сети и отправляя данные в зависимости от статуса заявки.
   */
  async createApplication() {
    await this.networkService.getStatus().then((status) => {
      if (!status) {
        this.alertService.showInfoAlert('Отсутствует подключение к интернету!');
        return;
      }
      this.stepManagerService.setStepLoaded(false);
      if (this.isApproved) {
        this.secondSendingData();
      } else {
        this.firstSendingData();
      }
    });
  }

  /**
   * Отправляет начальные данные заявки на кредит.
   * Определяет, нужно ли переключить флаг на оффлайн в зависимости от суммы и периода.
   */
  private firstSendingData() {
    const selectedPeriod = this.store.value.application.period;
    if (this.showMoreAmountMaxMessage || this.periodExceedsMaximumForOnline(selectedPeriod)) {
      this.store.changeIsOnlineFlag(false);
      this.store.value.application.isOnline = false;
    }

    this.store.createApplicationRequest$
      .pipe(
        map((request: CreateApplicationRequest) => {
          return request;
        }),
        switchMap((data: ApplicationOnCredit) => {
          return this.onlineCreditService.sendApplicationData(data);
        }),
        untilDestroyed(this)
      )
      .subscribe(
        (res: ApplicationOnCredit) => {
          this.store.updateApplication(res);
          if (this.showMoreAmountMaxMessage || this.periodExceedsMaximumForOnline(res.requestPeriod)) {
            void this.navigationService.push('/new-product/credit/choice-office', { replaceUrl: true });
          } else {
            void this.navigationService.push('/new-product/credit/personal-data', {
              replaceUrl: true,
              queryParams: { fillAdditionalPersonalData: false }
            });
          }
        },
        (error) => {
          const message = error.errorText || (error.error && error.error.errorText);
          this.alertService.showInfoAlert(message || error);
          this.stepManagerService.setStepLoaded(true);
        }
      );
  }

  /**
   * Обрабатывает повторное отправление данных после получения одобрения.
   */
  private secondSendingData() {
    this.store.calculationResult$
      .pipe(
        take(1), // Добавляем оператор take(1) здесь
        map((result) => {
          return {
            ...this.store.value.application,
            finalAmount: result.amount,
            finalPeriod: result.period,
            finalRate: result.interestRate,
            finalIsInsurance: result.insurance,
            finalInsuranceAmount: result.insurance ? +result.amountDks.toFixed(2) : 0,
            finalMonthAmount: +result.monthlyPayment.toFixed(2)
          };
        }),
        tap((finalResult) => {
          return this.store.updateApplication(finalResult);
        }),
        switchMap((finalResult) => {
          return this.onlineCreditService
            .createFinalRequest({
              ...this.store.value.application
            })
            .pipe(
              catchError((error) => {
                console.error('Error in createFinalRequest:', error);
                this.alertService.showInfoAlert(error.message || 'Не удалось создать финальную заявку');
                this.stepManagerService.setStepLoaded(true);
                return of(null); // Останавливаем выполнение, если произошла ошибка
              })
            );
        }),

        tap((finalId) => {
          this.store.updateApplication({ ...this.store.value.application, finalRequestId: finalId });
        }),
        switchMap(() => {
          return this.onlineCreditService.getApplicationData().pipe(
            catchError((error) => {
              console.error('Error in getApplicationData:', error);
              this.alertService.showInfoAlert('Не удалось получить данные заявки');
              this.stepManagerService.setStepLoaded(true);
              return throwError(error); // Или return of(null); если хотите завершить поток
            })
          );
        }),
        untilDestroyed(this)
        // Код обработки...
      )
      .subscribe(
        (res) => {
          this.store.updateApplication(res);
          void this.navigationService.push('/new-product/credit/confirm-final-request', {
            replaceUrl: true,
            queryParams: { isFinalRequest: true }
          });
          // Обработка успешного результата...
        },
        (error) => {
          const message = error.errorText || (error.error && error.error.errorText);
          this.alertService.showInfoAlert(message || error);
          this.stepManagerService.setStepLoaded(true);
        }
      );
  }

  /**
   * Проверяет, увеличился ли период кредитования.
   * @param newPeriod - Новый период кредитования.
   * @returns {boolean} Возвращает true, если период увеличился.
   */
  private periodHasBeenIncreased(newPeriod: number) {
    const { period } = this.store.value.calculation;
    return newPeriod > period;
  }

  /**
   * Извлекает значение суммы кредита из события.
   * @param event - Событие изменения суммы.
   * @returns {number} Значение суммы кредита.
   */
  private extractAmountValueFromEvent(event: any) {
    const isRangeEvent = event.detail;
    let value = isRangeEvent ? event.detail.value : event.target.value;

    if (!isRangeEvent) {
      value = +this.utilService.removeSpaces(value);
    }
    const { minAmount, maxAmount } = this.store.value.calculationConfig;

    if (value < minAmount) {
      value = minAmount;
    }
    if (value > maxAmount) {
      value = maxAmount;
    }

    if (!isRangeEvent) {
      event.target.value = value.toFixed(2);
    }
    return value;
  }

  /**
   * Извлекает значение периода кредитования из события.
   * @param event - Событие изменения периода.
   * @returns {number} Значение периода кредитования.
   */
  private extractPeriodValueFromEvent(event: any) {
    const isRangeEvent = event.detail;
    let value = isRangeEvent ? event.detail.value : event.target.value;
    const { minPeriod, maxPeriod } = this.store.value.calculationConfig;
    if (!isRangeEvent) {
      value = +this.utilService.removeSpaces(value);
    }
    if (value < minPeriod) {
      value = minPeriod;
    }
    if (value > maxPeriod) {
      value = maxPeriod;
    }

    if (!isRangeEvent) {
      event.target.value = '' + value;
    }

    return value;
  }

  /**
   * Выполняет расчет кредита в зависимости от суммы, периода и страховки.
   * @param amount - Сумма кредита.
   * @param period - Период кредитования.
   * @param withInsurance - Наличие страховки.
   * @param periodIncreased - Увеличился ли период.
   */
  private calculateLoan(amount: number, period: number, withInsurance: boolean, periodIncreased: boolean) {
    const { maxAmount, ...result } = this.loanCalculationService.calculateLoan(
      amount,
      period,
      withInsurance,
      periodIncreased
    );
    const { maxAmountOnline } = this.store.value.calculationConfig;
    if (maxAmount) {
      result.amount = amount > maxAmount ? maxAmount : result.amount;
      this.store.updateCalculationConfig({ maxAmount: maxAmount });
    }
    this.showMoreAmountMaxMessage = +result.amount.toFixed(0) > maxAmountOnline;
    this.store.updateCalculationResult(result);
  }

  /**
   * Показать предупреждение о прерывании процесса.
   */
  abort() {
    this.stepManagerService.showAbortAlert();
  }

  /**
   * Проверяет, превышает ли период максимальный допустимый период для онлайн-заявки.
   * @param period - Период кредитования.
   * @returns {boolean} Возвращает true, если период превышает допустимый для онлайн-заявки.
   */
  periodExceedsMaximumForOnline(period: number) {
    if (!this.loanCalculationService.periodForOnline) {
      return false;
    }

    return period > this.loanCalculationService.periodForOnline;
  }

  protected readonly MaskUtilService = MaskUtilService;
}
